import {CAMPAIGN_KEYS} from "@/api/campaignApi";
import {queryClient} from "@/queryClient";
import useCampaignManagementStore from "@/store/campaignManagement";
import {VIDEO_EXTENSIONS} from "@/store/constants";
import {create} from "@/store/createStore";
import type {
  TContent,
  TGalleryFilters,
  TInstagramContent,
  TMediaGalleryItem,
  TMediaType,
  TNonFunctionKeys,
  TPaginated,
  TPhotoExtended,
} from "@/store/types";
import {type InfiniteData, type QueryKey} from "@tanstack/react-query";
import type {AxiosResponse} from "axios";
import {debounce, isNull, type DebouncedFuncLeading} from "lodash";
import omitBy from "lodash/omitBy";
import qs from "qs";
import {getImageSize} from "react-image-size";
import {devtools} from "zustand/middleware";
import {immer} from "zustand/middleware/immer";

export const getFileExtension = (url: string) => {
  if (!url) {
    return null;
  }

  const regex = /\.([0-9a-z]+)(?:[\?#]|$)/i;
  const match = url.match(regex);

  return match ? match[1] : null;
};

type TState<TFilters extends TGalleryFilters> = {
  isLoading: boolean;
  calculatedImages: TPhotoExtended[];
  media: TInstagramContent[];
  searchParams: string | null;
  setImages(images: TMediaGalleryItem[], isFirstPage?: boolean): Promise<void>;
  setSearchParams(searchParams: string): void;
  setFilters<K extends keyof TFilters, V extends TFilters[K]>(key: K, value: V): void;
  resetMedia(): void;
  buildSearchParams(): string;
  debouncedSearchParams: DebouncedFuncLeading<() => void>;
  reset(): void;
};

const initialState = {
  isLoading: false,
  calculatedImages: [],
  media: [],
  searchParams: null,
} satisfies Pick<TState<TGalleryFilters>, TNonFunctionKeys<TState<TGalleryFilters>>>;

const initialFilters: TGalleryFilters = {
  date: [],
  gender: [],
  has_code: null,
  liked: null,
  media_product_type: null,
  ordering: null,
  purchased: null,
  search: "",
};

const defaultGetQueryKey = (searchParams: string | null): QueryKey =>
  CAMPAIGN_KEYS.qCampaignGallery(useCampaignManagementStore.getState().campaign.id, searchParams);

const createGalleryStore = <TFilters extends TGalleryFilters = TGalleryFilters>(
  filters: TFilters = initialFilters as TFilters,
  storePrefix: string = "",
  getQueryKey: (searchParams: string | null) => QueryKey = defaultGetQueryKey,
) =>
  create<TState<TFilters> & {filters: TFilters}>()(
    immer(
      devtools(
        (set, get) => ({
          ...initialState,
          filters,
          debouncedSearchParams: debounce(() => {
            get().setSearchParams(get().buildSearchParams());
          }, 1000),
          buildSearchParams: () => {
            const {filters} = get();
            return qs.stringify(omitBy(filters!, isNull), {
              arrayFormat: "comma",
            });
          },
          resetMedia: () =>
            set(
              state => {
                state.calculatedImages = [];
                state.media = [];
              },
              false,
              `useCampaignGallery${storePrefix}Store/resetMedia`,
            ),
          setFilters: (key, value) => {
            get().debouncedSearchParams.cancel();

            set(
              state => {
                (state.filters as TFilters)[key] = value;
              },
              false,
              `useCampaignGallery${storePrefix}Store/setFilters`,
            );
            const searchParams = get().buildSearchParams();
            const cachedQuery = queryClient.getQueryData<InfiniteData<AxiosResponse<TPaginated<TMediaGalleryItem>>>>(
              getQueryKey(searchParams),
            );

            if (cachedQuery?.pages) {
              get().setSearchParams(searchParams);
            } else {
              get().debouncedSearchParams();
            }
          },
          setSearchParams: searchParams => {
            if (get().searchParams === searchParams) {
              return;
            }

            set(
              state => {
                state.searchParams = searchParams;
              },
              false,
              `useCampaignGallery${storePrefix}Store/buildSearchParams`,
            );
            get().resetMedia();
          },
          setImages: async (images, isFirstPage = false) => {
            set(
              state => {
                state.isLoading = true;
              },
              false,
              "useCampaignGalleryStore/setLoading",
            );
            const data: TPhotoExtended[] = [];

            for (let index = 0; index < images.length; index++) {
              const type = images[index].type;
              const element = images[index].result;
              let dimensions = {width: 400, height: 400};
              try {
                const measured =
                  type === "TIKTOK"
                    ? {
                        width: element.thumbnail?.width!,
                        height: element.thumbnail?.height!,
                      }
                    : await getImageSize(
                        (element.thumbnail_file || element.thumbnail_url || element.media_file || element.media_url)!,
                      );

                if (measured.width > 0 && measured.height > 0) {
                  dimensions = measured;
                }
              } catch (error) {
                console.log(error);
              }

              data.push({
                src: (type === "TIKTOK"
                  ? element.thumbnail?.url || element.thumbnail_file
                  : element.thumbnail_file || element.thumbnail_url || element.media_file || element.media_url)!,
                width: dimensions.width,
                height: dimensions.height,
                file_type: getFileExtension(
                  (type === "TIKTOK"
                    ? element.video?.url || element.media_file
                    : element.media_file || element.media_url)!,
                ),
                type:
                  type === "TIKTOK"
                    ? "TIKTOK"
                    : type === "SOURCE"
                      ? "SOURCE"
                      : type === "UGC"
                        ? "UGC"
                        : element.media_product_type!,
                ...element,
                id: element.id,
                media_file: element.media_file,
                purchased: element.purchased,
                thumbnail_file: element.thumbnail_file,
                timestamp: type === "TIKTOK" ? element.create_time : element.timestamp,
              });
            }

            data.forEach(item => {
              if (!item.influencer && item.campaign_application?.user) {
                const user = item.campaign_application.user;

                item.influencer = {
                  picture320: user.influencer_profile?.picture320,
                  user: {
                    id: user.id,
                    first_name: user.first_name!,
                    last_name: user.last_name!,
                  },
                  uuid: user?.influencer_profile?.uuid,
                };
              }
            });
            set(
              state => {
                state.calculatedImages = [...data];
                state.media = [
                  ...(!isFirstPage ? state.media : []),
                  ...images.map(image => {
                    if (image.type === "TIKTOK") {
                      return {
                        applicationId: image.result.campaign_application?.id,
                        caption: image.result.caption,
                        id: image.result.id,
                        image: (image.result.thumbnail?.url || image.result.thumbnail_file)!,
                        instagram_media: {
                          campaign_application: image.result.campaign_application,
                          comments_count: image.result.metrics?.comments ?? image.result.comment_count,
                          created: image.result.created,
                          id: image.result.id,
                          like_count: image.result.metrics?.likes ?? image.result.digg_count,
                          liked: image.result.liked,
                          media_file: (image.result.video?.url ?? image.result.media_file)!,
                          media_product_type: "TIKTOK" as TContent,
                          media_type: "VIDEO" as TMediaType,
                          permalink: image.result.permalink,
                          play_count: image.result.metrics?.views ?? image.result.play_count,
                          purchased: image.result.purchased,
                          reach_count: image.result.metrics?.reach ?? image.result.reached_audience,
                          save_count: image.result.metrics?.saves ?? image.result.collect_count,
                          share_count: image.result.metrics?.shares ?? image.result.share_count,
                          thumbnail_file: (image.result.thumbnail?.url ?? image.result.thumbnail_file)!,
                          timestamp: image.result.create_time,
                          video_code: image.result.video_code,
                        },
                        user: {
                          first_name: image.result.influencer.user?.first_name,
                          id: image.result.influencer.user?.id,
                          influencer_id: image.result.influencer.uuid,
                          last_name: image.result.influencer.user?.last_name,
                          picture: image.result.influencer.picture320,
                          username: image.result.influencer.user?.username,
                        },
                      };
                    }

                    if (image.type === "SOURCE") {
                      return {
                        applicationId: image.result.campaign_application?.id,
                        id: image.result.id,
                        image: image.result.thumbnail_file!,
                        instagram_media: {
                          campaign_application: image.result.campaign_application,
                          created: image.result.created,
                          id: image.result.id,
                          liked: image.result.liked,
                          media_file: image.result.media_file,
                          media_product_type: "SOURCE" as TContent,
                          media_type: VIDEO_EXTENSIONS.includes(getFileExtension(image.result.media_file!)!)
                            ? "VIDEO"
                            : ("IMAGE" as TMediaType),
                          purchased: image.result.purchased,
                          thumbnail_file: image.result.thumbnail_file,
                        },
                        user: {
                          first_name: image.result.influencer.user?.first_name,
                          id: image.result.influencer.user?.id,
                          influencer_id: image.result.influencer.uuid,
                          last_name: image.result.influencer.user?.last_name,
                          picture: image.result.influencer.picture320,
                          username: image.result.influencer.user?.username,
                        },
                      };
                    }

                    return {
                      applicationId: image.result.campaign_application?.id,
                      caption: image.result.caption,
                      id: image.result.id,
                      image: image.result.thumbnail_file!,
                      instagram_media: {
                        campaign_application: image.result.campaign_application,
                        children: image.result.children,
                        comments_count: image.result.metrics?.comments ?? image.result.comments_count,
                        created: image.result.created,
                        id: image.result.id,
                        like_count: image.result.metrics?.likes ?? image.result.like_count,
                        liked: image.result.liked,
                        media_file: image.result.media_file,
                        media_product_type: (image.type === "UGC"
                          ? "UGC"
                          : image.result.media_product_type) as TContent,
                        media_type: image.result.media_type ?? "VIDEO",
                        media_url: image.result.media_url,
                        permalink: image.result.permalink,
                        play_count:
                          image.result.metrics?.views ?? image.result.impression_count ?? image.result.play_count,
                        purchased: image.result.purchased,
                        reach_count: image.result.metrics?.reach ?? image.result.reach_count,
                        save_count: image.result.metrics?.saves ?? image.result.saves_count,
                        share_count: image.result.metrics?.shares ?? image.result.shares_count,
                        thumbnail_file: image.result.thumbnail_file!,
                        thumbnail_url: image.result.thumbnail_url,
                        timestamp: image.result.timestamp,
                        video_code: image.result.video_code,
                      },
                      ugcInformation:
                        image.type === "UGC" ? {revisionStatus: image.result.status ?? undefined} : undefined,
                      user: {
                        first_name:
                          image.result.influencer?.user?.first_name ??
                          image.result.campaign_application?.user?.first_name,
                        id: image.result.influencer?.user?.id ?? image.result.campaign_application?.user?.id,
                        influencer_id:
                          image.result.influencer?.uuid ??
                          image.result.campaign_application?.user?.influencer_profile?.uuid,
                        last_name:
                          image.result.influencer?.user?.last_name ??
                          image.result.campaign_application?.user?.last_name,
                        picture:
                          image.result.influencer?.picture320 ??
                          image.result.campaign_application?.user?.influencer_profile?.picture320,
                        username: image.result.influencer?.user?.username,
                      },
                    };
                  }),
                ];
              },
              false,
              `useCampaignGallery${storePrefix}Store/setImages`,
            );

            set(
              state => {
                state.isLoading = false;
              },
              false,
              `useCampaignGallery${storePrefix}Store/setLoading`,
            );
          },
          reset: () =>
            set({...initialState, filters: {...filters}}, false, `useCampaignGallery${storePrefix}Store/reset`),
        }),
        {name: `useCampaignGallery${storePrefix}Store`},
      ),
    ),
  );

const useCampaignGalleryStore = createGalleryStore(initialFilters);
const useCampaignGalleryAdminStore = createGalleryStore(initialFilters, "Admin");

export {createGalleryStore, useCampaignGalleryAdminStore, useCampaignGalleryStore};
